import React, { useState } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import dayjs from 'dayjs';
import tenantApi, { HubspotApiSettings } from '../../../utils/api/tenant';
import { useTenantHubspotApi } from '../../../hooks/features';
import ConfirmationModal from '../../Shared/ConfirmationModal/ConfirmationModal';
import ToggleSmall from '../../Shared/Toggle/ToggleSmall';
import './_HubspotApiTab.scss';

const formatDate = (value: string | null | undefined) =>
  value ? dayjs(value).format('MMM D, YYYY') : 'Never';

type ConfirmAction = 'regenerate' | 'revoke' | 'resync';

// Copy for the shared confirmation modal, keyed by the pending action.
const CONFIRM_COPY: Record<
  ConfirmAction,
  { title: string; subTitle: string; buttonText: string }
> = {
  revoke: {
    title: 'Revoke token?',
    subTitle:
      'The token stops working immediately and cannot be restored. Your integration will lose access until a new token is generated.',
    buttonText: 'Revoke Token',
  },
  regenerate: {
    title: 'Regenerate token?',
    subTitle:
      'The current token stops working immediately. Your integration will fail until it’s updated with the new token.',
    buttonText: 'Regenerate',
  },
  resync: {
    title: 'Refresh HubSpot table?',
    subTitle:
      'This re-syncs every project in this tenant into the HubSpot data table. It’s safe to run — the integration only picks up genuinely changed records, so nothing is duplicated — but it can take a few minutes for large tenants. Project data already syncs automatically (in near-real-time and a full weekly refresh); only use this to force a refresh now, e.g. after a bulk data change.',
    buttonText: 'Refresh table',
  },
};

/**
 * SW2-2187 — HubSpot API tab on the Administration page. Tenant-level: the
 * endpoints take no id and act on the logged-in user's tenant.
 *
 * The plaintext token exists only in `revealToken` (component state, from the
 * POST response) and is unrecoverable after it is dropped — by design.
 */
const HubspotApiTab: React.FC = () => {
  const queryClient = useQueryClient();

  const settingsQuery = useTenantHubspotApi();
  const settings: HubspotApiSettings = settingsQuery.data ?? {
    enabled: false,
    token: null,
  };

  // One-time reveal (state B). Never persisted, never cached.
  const [revealToken, setRevealToken] = useState<string | null>(null);
  const [isCopied, setIsCopied] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [confirmAction, setConfirmAction] = useState<ConfirmAction | null>(null);

  const invalidate = () => queryClient.invalidateQueries('tenantHubspotApi');

  const toggleMutation = useMutation(
    (enabled: boolean) => tenantApi.hubspot.updateSettings({ enabled }),
    {
      onSuccess: (data) => {
        invalidate();
        toast.success(
          data.enabled
            ? 'HubSpot reporting API turned on.'
            : 'HubSpot reporting API turned off. Existing token kept but blocked.',
        );
      },
      onError: () => {
        toast.error('Could not update the HubSpot reporting API setting.');
      },
    },
  );

  const revokeMutation = useMutation(() => tenantApi.hubspot.revokeToken(), {
    onSuccess: () => {
      setRevealToken(null);
      invalidate();
      toast.success('Token revoked.');
    },
    onError: () => {
      toast.error('Could not revoke the token.');
    },
  });

  const resyncMutation = useMutation(() => tenantApi.hubspot.resync(), {
    onSuccess: (data) => {
      toast.success(
        `Re-sync started — ${data.projectsQueued} project${
          data.projectsQueued === 1 ? '' : 's'
        } queued. Changes appear in the feed within a few minutes.`,
      );
    },
    onError: () => {
      toast.error('Could not start the re-sync.');
    },
  });

  // Plain async on purpose: useMutation would retain the plaintext token in
  // react-query's mutation cache. Component state is the only copy.
  async function handleGenerate(successMessage: string) {
    setIsGenerating(true);
    try {
      const res = await tenantApi.hubspot.generateToken();

      setRevealToken(res.token);
      invalidate();
      toast.success(successMessage);
    } catch (err) {
      // 400 = toggle is off; surface the backend's message when it has one
      const message =
        err && err.response && err.response.data && err.response.data.message;

      toast.error(message || 'Could not generate a token.');
    } finally {
      setIsGenerating(false);
    }
  }

  // copy pattern from PublicURLNavbar (copyTextToClipboard + handleCopyClick)
  async function copyTextToClipboard(text: string) {
    if ('clipboard' in navigator) {
      return await navigator.clipboard.writeText(text);
    } else {
      return document.execCommand('copy', true, text);
    }
  }

  const handleCopyClick = () => {
    if (!revealToken) return;
    copyTextToClipboard(revealToken)
      .then(() => {
        setIsCopied(true);
        setTimeout(() => {
          setIsCopied(false);
        }, 1500);
      })
      .catch(() => {});
  };

  const handleConfirm = () => {
    const action = confirmAction;

    setConfirmAction(null);
    if (action === 'revoke') {
      revokeMutation.mutate();
    } else if (action === 'regenerate') {
      handleGenerate('Token regenerated. The previous token has been revoked.');
    } else if (action === 'resync') {
      resyncMutation.mutate();
    }
  };

  if (settingsQuery.isLoading) {
    return <div className='hubspot-api-tab'>Loading…</div>;
  }

  if (settingsQuery.isError) {
    const error: any = settingsQuery.error;
    const status = error && error.response && error.response.status;

    if (status === 403) {
      return (
        <div className='hubspot-api-tab'>
          <p>
            The HubSpot API feature is not enabled for this tenant. Enable it
            on the Features tab first.
          </p>
        </div>
      );
    }
    if (status === 401) {
      return (
        <div className='hubspot-api-tab'>
          <p>You do not have permission to manage the HubSpot API.</p>
        </div>
      );
    }

    return (
      <div className='hubspot-api-tab'>
        <p>Could not load the HubSpot API settings.</p>
        <button className='outline' onClick={() => settingsQuery.refetch()}>
          Retry
        </button>
      </div>
    );
  }

  const renderTokenBody = () => {
    if (revealToken) {
      // State B — one-time reveal, from the POST response only
      return (
        <>
          <div className='ha-reveal-banner'>
            <span aria-hidden='true'>&#9888;</span>
            <span>
              <strong>Copy this token now.</strong> It cannot be shown again.
              If it&rsquo;s lost, generate a new one.
            </span>
          </div>
          <div className='ha-token-box'>
            <div className='ha-token-value'>{revealToken}</div>
            <button className='primary' onClick={handleCopyClick}>
              {isCopied ? 'Copied!' : 'Copy token'}
            </button>
            <button
              className='outline'
              onClick={() => {
                setRevealToken(null);
                toast.success(
                  'Token hidden. Only the masked version is shown from now on.',
                );
              }}
            >
              I&rsquo;ve saved it
            </button>
          </div>
        </>
      );
    }

    if (settings.token) {
      // State C — active token, masked status from the backend
      const token = settings.token;

      return (
        <>
          <div className='ha-token-box'>
            <div className='ha-token-value'>
              {token.masked || `swhs_ •••• •••• •••• ${token.last4}`}
            </div>
            {token.expired && (
              <span className='ha-status-pill ha-status-pill--expired'>
                EXPIRED
              </span>
            )}
          </div>
          <div className='ha-meta-grid'>
            <div className='ha-meta-cell'>
              <p className='ha-meta-cell__key'>Created</p>
              <p className='ha-meta-cell__value'>{formatDate(token.createdAt)}</p>
            </div>
            <div className='ha-meta-cell'>
              <p className='ha-meta-cell__key'>Expires</p>
              <p
                className={`ha-meta-cell__value ${
                  token.expired
                    ? 'ha-meta-cell__value--expired'
                    : 'ha-meta-cell__value--soon'
                }`}
              >
                {formatDate(token.expiresAt)}
              </p>
            </div>
            <div className='ha-meta-cell'>
              <p className='ha-meta-cell__key'>Last used</p>
              <p className='ha-meta-cell__value'>{formatDate(token.lastUsedAt)}</p>
            </div>
            <div className='ha-meta-cell'>
              <p className='ha-meta-cell__key'>Scope</p>
              <p className='ha-meta-cell__value'>Read-only</p>
            </div>
          </div>
          <div className='ha-actions-row'>
            <button
              className='outline'
              disabled={isGenerating}
              onClick={() => setConfirmAction('regenerate')}
            >
              Regenerate token
            </button>
            <button
              className='outline warn'
              disabled={revokeMutation.isLoading}
              onClick={() => setConfirmAction('revoke')}
            >
              Revoke token
            </button>
          </div>
        </>
      );
    }

    // State A — no token
    return (
      <div className='ha-empty-token'>
        <button
          className='primary'
          disabled={isGenerating}
          onClick={() => handleGenerate('Token generated.')}
        >
          {isGenerating ? 'Generating…' : 'Generate token'}
        </button>
        <p>
          No token yet. Generate one and add it to your integration&rsquo;s
          request headers.
        </p>
      </div>
    );
  };

  return (
    <div className='hubspot-api-tab'>
      <div className='ha-card'>
        <div className='ha-setting-row'>
          <div>
            <h3 className='ha-card__title'>
              HubSpot reporting API{' '}
              <span
                className={`ha-status-pill ha-status-pill--${
                  settings.enabled ? 'on' : 'off'
                }`}
              >
                {settings.enabled ? 'ON' : 'OFF'}
              </span>
            </h3>
            <p className='ha-card__desc'>
              Allow an external integration (e.g. your n8n workflow) to read
              this tenant&rsquo;s reporting data through the HubSpot API
              endpoints. All endpoints are read-only.
            </p>
          </div>
          <ToggleSmall
            id='hubspot-api-toggle'
            isChecked={settings.enabled}
            disabled={toggleMutation.isLoading}
            handleChange={() => toggleMutation.mutate(!settings.enabled)}
          />
        </div>
      </div>

      <div
        className={`ha-card${settings.enabled ? '' : ' ha-card--disabled'}`}
      >
        <div className='ha-token-inner'>
          <h3 className='ha-card__title'>Access token</h3>
          <p className='ha-card__desc'>
            The integration authenticates with this token. It is scoped to this
            tenant only and expires 6 months after generation.
          </p>
          {renderTokenBody()}
          <div className='ha-howto'>
            Use it as a bearer token:{' '}
            <code>Authorization: Bearer swhs_…</code> against{' '}
            <code>GET /api/hubspot/v1/…</code> · Limit: 60 requests/min.
          </div>
        </div>
        {!settings.enabled && (
          <p className='ha-disabled-note'>
            {settings.token
              ? 'Reporting API is off — requests are rejected, but the token is kept. Turn the API back on to restore access.'
              : 'Turn on the HubSpot reporting API above to manage tokens.'}
          </p>
        )}
      </div>

      {settings.enabled && (
        <div className='ha-card'>
          <div className='ha-setting-row'>
            <div>
              <h3 className='ha-card__title'>Data sync</h3>
              <p className='ha-card__desc'>
                Project data syncs automatically — in near-real-time as records
                change, plus a full refresh every week. Use this only to force a
                full refresh now, e.g. after a bulk data change or if the feed
                looks out of date.
              </p>
            </div>
            <button
              className='outline'
              disabled={resyncMutation.isLoading}
              onClick={() => setConfirmAction('resync')}
            >
              {resyncMutation.isLoading ? 'Refreshing…' : 'Refresh HubSpot table'}
            </button>
          </div>
        </div>
      )}

      <ConfirmationModal
        show={confirmAction !== null}
        title={confirmAction ? CONFIRM_COPY[confirmAction].title : ''}
        subTitle={confirmAction ? CONFIRM_COPY[confirmAction].subTitle : ''}
        buttonText={confirmAction ? CONFIRM_COPY[confirmAction].buttonText : ''}
        handleClose={() => setConfirmAction(null)}
        handleConfirm={handleConfirm}
      />
    </div>
  );
};

export default HubspotApiTab;
